import { useTranslation } from "@i18n";

/** One option of the version / issue-type dropdowns in this modal. */
type AttachIssuesOption = { id?: string; text?: string };

/** The synthetic event this modal builds for its dropdown handlers. */
type AttachIssuesChange = {
  value?: string | string[];
  target?: { value?: string };
  itemData?: AttachIssuesOption | null;
};
import { useState } from "react";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";

function AttachIssues({
  wrapperStyles = {},
  handleSetOpenAttachIssuesModal,
  onSuccess,
}: {
  /** Caller-supplied class-name overrides, keyed by slot — not CSS properties. */
  wrapperStyles?: { fileManagerModalOverlayWrapper?: string };
  handleSetOpenAttachIssuesModal: (open: boolean) => void;
  /**
   * METHOD syntax, not a property arrow: `FileManagerAsPopup` supplies a handler declaring its own
   * richer payload (`filesIssues`, `isSelected`). Method signatures are checked bivariantly even under
   * `strictFunctionTypes`, so that handler stays assignable without a cast at the call site.
   */
  onSuccess(data: { selectedVersionType?: string; selectedIssueType?: string[] }): void;
}) {
  const { t } = useTranslation();
  const [selectedRadioOption, setSelectedRadioOption] = useState("attach_issues");
  const [selectedVersionType, setSelectedVersionType] = useState("current_version");
  const [selectedIssueType, setSelectedIssueType] = useState(["open", "in_review"]);

  const fileVersionsDataSource = [
    { id: "current_version", text: "Current version" },
    { id: "previous_version", text: "Previous versions except current" },
    { id: "all", text: "All previous versions including current" },
  ];

  const issueStatusesDataSource = [
    { id: "open", text: "Open" },
    { id: "in_review", text: "In review" },
    { id: "closed", text: "Closed" },
  ];

  const handleConfirmButtonClick = () => {
    onSuccess({
      selectedVersionType,
      selectedIssueType,
    });
    handleSetOpenAttachIssuesModal(false);
  };

  const handleCancelButtonClick = () => {
    handleSetOpenAttachIssuesModal(false);
  };

  const handleRadioOptionChange = (args: AttachIssuesChange) => {
    if (args.value == "no_issues") {
      setSelectedVersionType("");
      setSelectedIssueType([]);
    }
    setSelectedRadioOption(args.value as string);
  };

  const handleVersionTypeChange = (args: AttachIssuesChange) => {
    setSelectedVersionType(args.target!.value!);
  };

  const handleIssueTypeChange = (args: AttachIssuesChange) => {
    setSelectedIssueType(args.value as string[]);
  };

  return (
    <div
      className={`fixed start-0 top-[4.6875rem] z-[21] flex h-full w-full items-center justify-center overflow-scroll bg-[var(--background-scrim)] ${
        wrapperStyles.fileManagerModalOverlayWrapper || ""
      }`}
    >
      <div className="flex w-[29.25rem] flex-col gap-3 bg-white p-4 text-sm">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">{t("Attach Issues")}</div>
          <span className="cursor-pointer" onClick={handleCancelButtonClick}>
            {"✕"}
          </span>
        </div>
        <div>{t("The file(s) that you have selected contain(s) issue(s)")}</div>
        <div>
          <div
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <RadioButton
              onChange={handleRadioOptionChange}
              checked={selectedRadioOption == "attach_issues"}
              value="attach_issues"
              label="Attach issues"
              name="issues_option"
            />
          </div>
          <div className="flex gap-8 pt-3 ps-7">
            <div className="flex flex-1 flex-col gap-3">
              <span>{t("File Version")}</span>
              <div
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <AutoComplete
                  onChange={(_event: unknown, newValue: AttachIssuesOption | null) =>
                    handleVersionTypeChange({
                      target: { value: newValue?.id },
                      value: newValue?.id,
                      itemData: newValue,
                    })
                  }
                  value={
                    fileVersionsDataSource.find((opt: AttachIssuesOption) => opt.id === selectedVersionType) || null
                  }
                  valueField="id"
                  getOptionLabel={(option: AttachIssuesOption) => option?.text || ""}
                  options={fileVersionsDataSource}
                  renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select type")} />}
                />
              </div>
            </div>
            <div className="flex flex-[0.5] flex-col gap-3">
              <span>{t("Issue Status")}</span>
              <div
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <MultiSelectComponent
                  value={selectedIssueType}
                  dataSource={issueStatusesDataSource}
                  fields={{ text: "text", value: "id" }}
                  placeholder={t("All status")}
                  change={handleIssueTypeChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
        >
          <RadioButton
            checked={selectedRadioOption == "no_issues"}
            value="no_issues"
            onChange={handleRadioOptionChange}
            label="Don't attach issues"
            name="issues_option"
          />
        </div>
        <div className="flex gap-3" onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
          <ButtonComponent buttonType="gradientButton" onClick={handleConfirmButtonClick} cssClass="e-info">
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent onClick={handleCancelButtonClick} cssClass="e-flat">
            {t("Cancel")}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );
}

export default AttachIssues;
